import { apiFetch } from './client';

// ── Stripe settings ──────────────────────────────────────────────────────────

export interface StripeConfigView {
  mode: 'test' | 'live' | null;
  secretKey_hint: string | null;
  publicKey_hint: string | null;
  webhookSecret_set: boolean;
}

export interface StripeConfigInput {
  mode: 'test' | 'live';
  secretKey: string;
  webhookSecret: string;
  publicKey: string;
}

export interface StripeTestResult {
  ok: boolean;
  accountId?: string;
  displayName?: string | null;
  error?: string;
  message?: string;
}

// ── Plans ────────────────────────────────────────────────────────────────────

export interface UsageSummaryItem {
  meter: string;
  quantity: number;
  included: number;
  overage: number;
  overage_rate_cents: number;
  overage_cost_cents: number;
}

export interface BillingSummary {
  plan: {
    id: string;
    name: string;
    monthly_price_cents: number;
  } | null;
  billing_status: string;
  stripe_customer_id: string | null;
  usage: UsageSummaryItem[];
  estimated_total_cents: number;
}

export interface InvoiceListItem {
  id: string;
  number: string | null;
  amount_due: number;
  amount_paid: number;
  status: string | null;
  created: number;
  period_start: number;
  period_end: number;
  hosted_invoice_url: string | null;
  invoice_pdf: string | null;
}

export interface PaymentMethod {
  id: string;
  brand: string | null;
  last4: string | null;
  exp_month: number | null;
  exp_year: number | null;
}

export interface SubscriptionStatus {
  reseller_name: string;
  plan_name: string | null;
  monthly_price_cents: number | null;
  billing_status: string;
  stripe_subscription_id: string | null;
}

export interface AdminBillingRow {
  reseller_id: string;
  reseller_name: string;
  plan_id: string | null;
  plan_name: string | null;
  monthly_price_cents: number | null;
  billing_status: string;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
}

export interface AdminBillingOverview {
  rows: AdminBillingRow[];
  kpis: {
    total_mrr_cents: number;
    active_count: number;
    past_due_count: number;
    canceled_count: number;
    none_count: number;
  };
}

// ── Stripe prices (plan editor picker) ───────────────────────────────────────

export interface StripePriceRow {
  id: string;
  nickname: string | null;
  product_name: string | null;
  /** Cents. Null for tiered/metered prices. */
  unit_amount: number | null;
  currency: string;
  /** 'month' | 'year' | 'week' | 'day' — null for one-time prices. */
  recurring_interval: string | null;
}

export interface StripePriceList {
  rows: StripePriceRow[];
  /** false = platform Stripe credentials not configured yet — the plan
   *  dialog falls back to a manual price-id input. */
  stripe_configured: boolean;
}

export const billing = {
  // Stripe settings
  getStripeConfig: () => apiFetch<StripeConfigView>('/api/platform-settings/stripe'),
  putStripeConfig: (input: StripeConfigInput) =>
    apiFetch<{ ok: boolean }>('/api/platform-settings/stripe', {
      method: 'PUT',
      body: JSON.stringify(input),
    }),
  testStripeConfig: () => apiFetch<StripeTestResult>('/api/platform-settings/stripe/test', { method: 'POST' }),

  // Stripe prices — super_admin only; feeds the PlanDialog price Select.
  listStripePrices: () => apiFetch<StripePriceList>('/api/billing/stripe/prices'),

  // Reseller billing
  getSummary: (resellerId?: string) =>
    apiFetch<BillingSummary>(`/api/billing/summary${resellerId ? `?resellerId=${resellerId}` : ''}`),
  listInvoices: (resellerId?: string) =>
    apiFetch<{ rows: InvoiceListItem[] }>(`/api/billing/invoices${resellerId ? `?resellerId=${resellerId}` : ''}`),
  createSetupIntent: (resellerId?: string) =>
    apiFetch<{ clientSecret: string }>(`/api/billing/setup-intent${resellerId ? `?resellerId=${resellerId}` : ''}`, { method: 'POST' }),
  listPaymentMethods: (resellerId?: string) =>
    apiFetch<{ rows: PaymentMethod[] }>(`/api/billing/payment-methods${resellerId ? `?resellerId=${resellerId}` : ''}`),

  // Subscription management
  assignPlan: (resellerId: string, planId: string) =>
    apiFetch<{ clientSecret: string | null; stripePublicKey: string; billingStatus: string }>(
      `/api/resellers/${resellerId}/subscription`,
      { method: 'POST', body: JSON.stringify({ planId }) },
    ),
  getSubscriptionStatus: (resellerId: string) =>
    apiFetch<SubscriptionStatus>(`/api/resellers/${resellerId}/subscription`),
  cancelSubscription: (resellerId: string) =>
    apiFetch<void>(`/api/resellers/${resellerId}/subscription`, { method: 'DELETE' }),

  // Super-admin overview
  getAdminOverview: () => apiFetch<AdminBillingOverview>('/api/admin/billing/overview'),
};
